import React, { useRef, useState } from "react";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { fetchHandshake } from "@/services/handshake";
import { useTranslation } from "next-i18next";
import BreadCrumb from "@/components/global/BreadCrumb";
import { Form, Formik, ErrorMessage, Field } from "formik";
import Input from "@/components/forms/Input";
import Loading from "@/components/global/Loading";
import Select from "react-select";
import { customStyles } from "@/styles/select.style";
import { contactUsValidation } from "@/utils/valitation/contact-us";
import { useRouter } from "next/router";
import useSWR from "swr";
import {
  getContactUsFields,
  submitContactUsForm,
} from "@/services/static/contact-us";
import { getCookie } from "cookies-next";
import toast from "react-hot-toast";
import Link from "next/link";
import Image from "@/components/global/Image";
import Seo from "@/components/seo";

const ContactUsPage = () => {
  const { t } = useTranslation("contact-us");
  const { locale, push } = useRouter();
  const handshake = getCookie("handshake");
  const aboutSubjectRef = useRef(null);

  const [subjectOptions, setSubjectOptions] = useState([]);
  const [aboutSubjectOptions, setAboutSubjectOptions] = useState([]);

  const { data } = useSWR(
    () => `/contact-us-${locale}`,
    () => getContactUsFields(handshake, locale),
    {
      revalidateOnFocus: false,
      onSuccess: (res) => {
        setSubjectOptions(
          Object.entries(res?.data?.data?.Subjects).map((el) => ({
            label: el[0],
            value: el[1],
          })) || []
        );
      },
    }
  );

  const BreadCrumbItems = [
    {
      name: t("Contact us"),
      url: "/contact-us",
      active: true,
      key: "contact-us",
    },
  ];

  const handleContactUs = async (values, { setSubmitting }) => {
    setSubmitting(true);
    const { data, error } = await submitContactUsForm(handshake, values);
    console.log("test", error);
    if (error?.error) {
      toast.error(error?.error?.message);
    }
    if (data?.data) {
      toast.success(data?.data?.message);
      push("/");
    }
    setSubmitting(false);
  };

  const selectStyle = {
    ...customStyles,
    placeholder: (base) => ({
      ...base,
      fontSize: "14px",
    }),
  };

  return (
    <>
      <Seo title={locale === "en" ? "Contact Us" : "تواصل معنا"} />
      <BreadCrumb items={BreadCrumbItems} />
      <section className={`px-1 py-5 md:px-2 w-11/12 mx-auto`}>
        <h1 className="md:text-3xl font-semibold mb-3 md:mb-8">
          {t("Get In Touch")}
        </h1>
        <p className="text-xs md:text-lg font-semibold leading-5 md:leading-10 mb-5 md:mb-10">
          {t("You can send an email to")}{" "}
          <Link
            href="mailto:e-customercare@abdulwahed.com"
            className="text-xs md:text-xl font-bold text-nowrap"
          >
            e-customercare@abdulwahed.com
          </Link>{" "}
          {t("، or call us or contact us via")}{" "}
          <span className="text-xs md:text-xl">
            {t("whatsapp on")}{" "}
            <Link
              className="text-xs md:text-xl font-bold"
              href={"tel:8002440277"}
            >
              8002440277
            </Link>
          </span>{" "}
          <br /> {t("or you can send us a message using the following form")}
        </p>

        <Formik
          onSubmit={handleContactUs}
          initialValues={{
            name: "",
            email: "",
            telephone: "",
            subject: "",
            subsubject: "",
            comment: "",
          }}
          validationSchema={contactUsValidation(t)}
        >
          {({ isSubmitting, setFieldValue }) => (
            <Form>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                <Input
                  name="name"
                  id="name"
                  type="text"
                  label={t("Your Name")}
                  labelClassName="text-base text-[#74787C] mb-3"
                  inputClassName="border p-1.5 max-sm:text-sm max-sm:placeholder:text-sm w-full rounded-md outline-none"
                  t={t}
                />
                <Input
                  name="email"
                  id="email"
                  type="email"
                  label={t("Email")}
                  labelClassName="text-base text-[#74787C] mb-3"
                  inputClassName="border p-1.5 max-sm:text-sm max-sm:placeholder:text-sm w-full rounded-md outline-none"
                  t={t}
                />
                <div>
                  <p className="text-base text-[#74787C] mb-3">{t("Phone")}</p>
                  <div
                    dir="ltr"
                    className="flex items-center gap-1 border rounded-md overflow-hidden"
                  >
                    <Image
                      loading="eager"
                      className="mx-2 rounded-sm"
                      src="/imgs/KSA.svg"
                      height={30}
                      width={30}
                      alt="KSA Img !"
                    />
                    <span className="text-zinc-500 mx-1.5 max-sm:text-sm">
                      +966
                    </span>

                    <Field
                      name="telephone"
                      id="telephone"
                      type="tel"
                      placeholder="5xxxxxxxx"
                      maxLength={9}
                      className="outline-none border-none p-1.5 max-sm:placeholder:text-sm max-sm:text-sm flex-1"
                      t={t}
                    />
                  </div>

                  <ErrorMessage
                    name="telephone"
                    component="p"
                    className="text-sm text-warning"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                <div className="flex flex-col gap-3">
                  <label className="text-base text-[#74787C]">
                    {t("Subject")}
                  </label>
                  <Select
                    name="subject"
                    id="subject"
                    onChange={(e) => {
                      setFieldValue("subject", e.label);

                      aboutSubjectRef.current.clearValue();
                      setFieldValue("subsubject", "");

                      setAboutSubjectOptions(
                        data?.data?.data?.Topics?.[e.value]
                          ?.filter(
                            (e) =>
                              e !== "خدمات التوصيل سمسا" &&
                              e !== "SMSA Delivery Services"
                          )
                          ?.map((el) => ({
                            label: el,
                            value: el,
                          }))
                      );
                    }}
                    options={subjectOptions}
                    placeholder={t("Select Subject")}
                    styles={selectStyle}
                  />
                  <ErrorMessage
                    name={"subject"}
                    component="p"
                    className="mb-1 text-sm text-warning max-sm:text-xs"
                  />
                </div>

                <div className="flex flex-col gap-3">
                  <label className="text-base text-[#74787C]">
                    {t("About Subject")}
                  </label>
                  <Select
                    ref={aboutSubjectRef}
                    name="subsubject"
                    id="subsubject"
                    onChange={(e) => {
                      setFieldValue("subsubject", e?.label);
                    }}
                    options={aboutSubjectOptions}
                    placeholder={t("Select About Subject")}
                    styles={selectStyle}
                  />
                  <ErrorMessage
                    name={"subsubject"}
                    component="p"
                    className="mb-1 text-sm text-warning max-sm:text-xs"
                  />
                </div>
              </div>

              <Input
                name="comment"
                id="comment"
                label={t("Write Your Message")}
                as="textarea"
                rows={5}
                labelClassName="text-base text-[#74787C] mb-3"
                inputClassName="border p-1.5 max-sm:text-sm max-sm:placeholder:text-sm w-full rounded-md outline-none mb-4"
                t={t}
              />

              <button
                className="bg-primary text-white rounded-md font-semibold disabled:cursor-not-allowed max-sm:text-xs px-16 py-1.5"
                type="submit"
              >
                {isSubmitting ? <Loading /> : t("Send Message")}
              </button>
            </Form>
          )}
        </Formik>
      </section>
    </>
  );
};

export default ContactUsPage;

export async function getServerSideProps({ locale, req, res }) {
  // get handshake from cookies or generate new handshake
  await fetchHandshake({ req, res, locale });

  return {
    props: {
      ...(await serverSideTranslations(locale, ["common", "contact-us"])),
    },
  };
}
